import { useEffect, useMemo, useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, RefreshCw, Wrench } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  Drawer,
  ProductPicker,
  SearchInput,
  Select,
  Toggle,
  Toolbar,
  ToolbarCount,
  ToolbarLeft,
  ToolbarRight,
  toast,
  type PickerValue,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { useCreateOnboarding, useOnboardingList, useWaitingForMe } from '../api/onboarding-queries'
import type { OnboardingStatus, OnboardingSummary } from '../api/onboarding-types'
import { formatDate } from '../utils/dates'
import { EDIT_ROLES, STATUS_LABEL, STATUS_VARIANT } from '../utils/onboarding-meta'

/**
 * New-product requests. "New product" starts a blank request; "Fix an
 * existing product" starts one pre-filled from a product that was created
 * by hand in Odoo, so it can be completed, reviewed and corrected.
 */
export function OnboardingList({ onOpen }: { onOpen: (id: string) => void }) {
  const canEdit = useHasAnyPermission([...EDIT_ROLES])
  const [text, setText] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<OnboardingStatus | ''>('')
  const [includeClosed, setIncludeClosed] = useState(false)
  const [fixOpen, setFixOpen] = useState(false)
  const [fixProduct, setFixProduct] = useState<PickerValue>(null)
  const create = useCreateOnboarding()

  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 250)
    return () => clearTimeout(id)
  }, [text])

  const { data, isLoading, isError, error, refetch, isFetching } = useOnboardingList({ status, q, includeClosed })
  const rows = data?.items ?? []

  const start = (body: { odoo_product_tmpl_id?: number }) =>
    create.mutate(body, {
      onSuccess: (res) => {
        setFixOpen(false)
        onOpen(res.id)
      },
      onError: (err) => toast.danger('Could not start the request', { description: (err as Error).message }),
    })

  const columns = useMemo<ColumnDef<OnboardingSummary, unknown>[]>(
    () => [
      { id: 'ref', header: 'Request', cell: (ctx) => ctx.row.original.reference },
      {
        id: 'product',
        header: 'Product',
        cell: (ctx) => (
          <span>
            {ctx.row.original.name ?? <span className="text-tertiary">Untitled</span>}
            {ctx.row.original.mode === 'update_existing' ? (
              <Chip variant="neutral" className="ml-2">Fix existing</Chip>
            ) : null}
          </span>
        ),
      },
      { id: 'code', header: 'Code', cell: (ctx) => ctx.row.original.default_code ?? '—' },
      { id: 'vendor', header: 'Vendor', cell: (ctx) => ctx.row.original.vendor_name ?? '—' },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => (
          <Chip variant={STATUS_VARIANT[ctx.row.original.status]} dot>
            {STATUS_LABEL[ctx.row.original.status]}
          </Chip>
        ),
      },
      { id: 'by', header: 'Started by', cell: (ctx) => ctx.row.original.created_by ?? '—' },
      { id: 'updated', header: 'Updated', cell: (ctx) => formatDate(ctx.row.original.updated_at) },
    ],
    [],
  )

  return (
    <>
      <Toolbar>
        <SearchInput
          containerClassName="flex-1 max-w-none"
          placeholder="Search product, code or request number…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          aria-label="Search requests"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as OnboardingStatus | '')}>
            <option value="">All open</option>
            {(Object.keys(STATUS_LABEL) as OnboardingStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </Select>
          <Toggle label="Include finished and cancelled" checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)} disabled={!!status} />
        </ToolbarLeft>
        <ToolbarRight>
          <ToolbarCount>{rows.length} requests</ToolbarCount>
          <Button variant="ghost" size="sm" iconOnly aria-label="Refresh" loading={isFetching && !isLoading}
            onClick={() => void refetch()}>
            <RefreshCw size={14} />
          </Button>
          {canEdit ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setFixOpen(true)}>
                <Wrench size={14} /> Fix an existing product
              </Button>
              <Button variant="primary" size="sm" loading={create.isPending && !fixOpen} onClick={() => start({})}>
                <Plus size={14} /> New product
              </Button>
            </>
          ) : null}
        </ToolbarRight>
      </Toolbar>
      <div className="flex min-h-0 flex-1 flex-col gap-4 bg-canvas p-4">
        <WaitingForYou onOpen={onOpen} />
        <DataTable
          data={rows}
          columns={columns}
          loading={isLoading}
          error={isError ? error : undefined}
          onRetry={() => void refetch()}
          errorSubject="new product requests"
          getRowId={(r) => r.id}
          onRowClick={(r) => onOpen(r.id)}
          criticalRowPredicate={(r) => r.status === 'push_failed'}
          moduleId="products-onboarding"
        />
      </div>
      <Drawer
        surface="form"
        open={fixOpen}
        onOpenChange={setFixOpen}
        title="Fix an existing product"
        meta="For a product that was set up by hand in Odoo"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setFixOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={!fixProduct} loading={create.isPending}
              onClick={() => fixProduct && start({ odoo_product_tmpl_id: fixProduct[0] })}>
              Start request
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3 p-4">
          <p className="text-sm text-secondary">
            Its current Odoo values are copied into a new request. The checklist shows what is missing; complete it and
            send it through both sign-offs. On approval the existing Odoo product is corrected.
          </p>
          <ProductPicker
            value={fixProduct}
            onChange={setFixProduct}
            placeholder="Search products…"
            width={380}
            searchEndpoint="/api/product-documents/search/products"
          />
        </div>
      </Drawer>
    </>
  )
}


/** Requests at the signed-in person's step, so nobody has to scan the list. */
function WaitingForYou({ onOpen }: { onOpen: (id: string) => void }) {
  const { data } = useWaitingForMe()
  if (!data?.count) return null
  return (
    <section aria-label="Waiting for you" className="rounded-lg border border-warning-border bg-warning-bg px-4 py-3">
      <h2 className="text-md font-semibold text-warning-text">
        Waiting for you ({data.count})
      </h2>
      <ul className="mt-2 flex flex-col gap-1">
        {data.items.map((w) => (
          <li key={w.id}>
            <button type="button" onClick={() => onOpen(w.id)}
              className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-md hover:bg-surface-hover">
              <span className="w-24 text-secondary">{w.reference}</span>
              <span className="flex-1 font-medium text-primary">{w.name ?? 'Untitled'}</span>
              <Chip variant="warning">{w.why}</Chip>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
